<template>
  <div class="pointer-events-auto fixed top-16 right-[52px] z-30 w-64 p-2.5 rounded-md bg-sheet border border-border shadow-ink flex flex-col gap-1.5">
    <div class="flex items-center gap-1.5">
      <input
        v-model="draft.name"
        type="text"
        :placeholder="t('map.routeEditor.waypointName')"
        class="flex-1 h-8 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] text-[12px] outline-none"
      />
      <button
        type="button"
        class="shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger)/0.1)]"
        :title="t('map.routeEditor.deleteWaypoint')"
        :aria-label="t('map.routeEditor.deleteWaypoint')"
        @click="$emit('delete')"
      ><Trash2 class="w-3.5 h-3.5" /></button>
      <button
        type="button"
        class="shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
        :title="t('map.routeEditor.closeWaypoint')"
        :aria-label="t('map.routeEditor.closeWaypoint')"
        @click="$emit('close')"
      ><X class="w-4 h-4" /></button>
    </div>
    <div class="relative">
      <select
        v-model="draft.poi_type"
        class="w-full h-8 pl-2.5 pr-6 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] text-[12px] outline-none appearance-none cursor-pointer"
      >
        <option v-for="type in typeOptions" :key="type" :value="type">{{ t(`poiTypes.${type}`) }}</option>
      </select>
      <ChevronDown class="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[hsl(var(--muted-foreground))] pointer-events-none" />
    </div>
    <textarea
      v-model="draft.description"
      rows="2"
      :placeholder="t('map.routeEditor.waypointDescription')"
      class="w-full px-2.5 py-1.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] text-[12px] outline-none resize-none"
    />
    <button
      type="button"
      class="h-8 rounded-md bg-[hsl(var(--primary))] text-white text-[12px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors"
      @click="save"
    >{{ t('map.routeEditor.save') }}</button>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Trash2, ChevronDown, X } from "lucide-vue-next";
import { EDITOR_POI_TYPES, type PoiType } from "../types/poiTypes";
import type { EditorWaypoint } from "../composables/useRouteEditor";

const props = defineProps<{
  waypoint: EditorWaypoint;
}>();

const emit = defineEmits<{
  (e: "update", patch: Partial<Pick<EditorWaypoint, "name" | "description" | "poi_type">>): void;
  (e: "delete"): void;
  (e: "close"): void;
}>();

const { t } = useI18n();

const draft = reactive({
  name: props.waypoint.name,
  description: props.waypoint.description || "",
  poi_type: props.waypoint.poi_type,
});

// В списке только ночёвка/привал; уже сохранённая точка другого типа
// (например, из старого маршрута) остаётся выбранной, чтобы select не
// показывал пустое значение и тип не терялся при редактировании описания.
const typeOptions = computed<PoiType[]>(() => {
  const options: PoiType[] = [...EDITOR_POI_TYPES];
  return options.includes(draft.poi_type) ? options : [draft.poi_type, ...options];
});

watch(
  () => props.waypoint.clientId,
  () => {
    draft.name = props.waypoint.name;
    draft.description = props.waypoint.description || "";
    draft.poi_type = props.waypoint.poi_type;
  }
);

// Правки живут в draft и применяются только по "Сохранить"; крестик закрывает
// карточку, ничего не применяя (draft сбрасывается при следующем открытии -
// см. watch по clientId выше и :key в MapView.vue).
const save = () => {
  const patch = {
    name: draft.name.trim() || props.waypoint.name,
    description: draft.description.trim() || null,
    poi_type: draft.poi_type,
  };
  const changed =
    patch.name !== props.waypoint.name ||
    patch.description !== (props.waypoint.description || null) ||
    patch.poi_type !== props.waypoint.poi_type;
  if (changed) emit("update", patch);
  emit("close");
};
</script>
